import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { inject } from 'mobx-react'
import React from 'react'
// tslint:disable-next-line: no-var-requires
const CurrencyFormat: any = require('react-currency-format')
import { AttachMoney, Warning } from '@mui/icons-material'
import { Form, Formik } from 'formik'
import _ from 'lodash'
import * as Yup from 'yup'
import DatePickerField from '../../common/DatePickerField/DatePickerField'
import FullWidthField from '../../common/FullWidthField'
import ResponsiveAppBar from '../../common/ResponsiveAppBar'
import { ICheck } from '../../Definitions'
import { IProviderFormValues, toIApplyPayment } from '../../providers/ProviderFormValues'
import { IStores } from '../../Stores'
import { IBillTableFormValues } from '../billsTable/BillTableFormValues'
import { applyPayments } from 'src/services/ClaimService'
import { IApplyPaymentRequest } from 'src/viewModels/ApplyPaymentRequest'
import { Helmet } from 'react-helmet'
import moment from 'moment'
import { SentCheckSummaryResult } from 'src/viewModels/SentCheckSummaryResult'
import AssignedCheckModel from 'src/bills/writeChecks/AssignedCheckModal'

const CheckSchema = Yup.object().shape({
  providers: Yup.array().of(
    Yup.object({
      checkNumber: Yup.string().required('Check Number is required'),
      datePaid: Yup.date()
        .typeError('Invalid date')
        .test('valid-year', 'Year must be between 2000 and 2100', (value) => {
          if (!value) return true
          const year = moment(value).year()
          return year >= 2000 && year <= 2100
        })
        .required('Check Date is required'),
    })
  ),
})

export interface IWriteChecksProps {
  selectedBills?: IBillTableFormValues
  previewEOB?: (check: ICheck) => Promise<void>
  rerouteToPath?: (path: string) => void
  saveCheck?: (check: ICheck) => Promise<void>
  openAssignedCheck?: (results: SentCheckSummaryResult[]) => void
}

class WriteChecks extends React.Component<IWriteChecksProps> {
  public state = {
    isSaved: false,
    reload: false,
    isProcessing: false,
  }

  public handleSave = (submitForm: () => void, reload: boolean) => () => {
    if (this.state.isProcessing) return
    this.setState({ reload, isProcessing: true }, () => {
      submitForm()
    })
  }

  private save = async (values: IBillTableFormValues) => {
    try {
      let listOfPayments: IApplyPaymentRequest[] = []

      values.providers.forEach((x) => {
        listOfPayments = listOfPayments.concat(toIApplyPayment(x))
      })

      const _response = await applyPayments(listOfPayments)
      this.setState({ isSaved: true, isProcessing: false })
      this.props.openAssignedCheck!(_response)
    } catch (error) {
      console.error('Error applying payments:', error)
      this.setState({ isProcessing: false })
    }
  }

  public componentDidMount() {
    this.setState({ reload: false, isProcessing: false })
    if (!this.props.selectedBills) {
      this.props.rerouteToPath!('/payproviders')
    }
  }

  private getInitialValues(): IBillTableFormValues | undefined {
    if (!this.props.selectedBills) {
      return undefined
    }

    const defaultDate = moment(new Date()).format('yyyy-MM-DD')

    return {
      ...this.props.selectedBills,
      providers: this.props.selectedBills.providers.map((provider) => ({
        ...provider,
        datePaid: provider.datePaid || defaultDate,
        checkNumber: provider.checkNumber || '',
      })),
    }
  }

  private calculateProviderTotal(provider: IProviderFormValues) {
    let total = 0

    provider
      .cmS1500s!.filter((x) => x.isChecked)
      .forEach((y) => {
        total += y.q24
          .map((z) => (z.overrideAmount ? z.overrideAmount : +z.contractAmount!))
          .reduce((a, b) => a + b, 0)
      })

    return total
  }

  private cancel = () => {
    this.props.rerouteToPath!('/payproviders')
  }

  private getTotal(values: IBillTableFormValues) {
    let total = 0

    values.providers.forEach((x) => {
      total += this.calculateProviderTotal(x)
    })

    return total
  }

  public render() {
    const { selectedBills } = this.props
    return (
      <>
        <Helmet>
          <title>{'Check Summary'}</title>
        </Helmet>
        {selectedBills ? (
          <Formik
            initialValues={this.getInitialValues()!}
            onSubmit={this.save}
            enableReinitialize={true}
            validationSchema={CheckSchema}
            validateOnMount={true}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({ values, submitForm, isValid }) => {
              return (
                <>
                  {this.state.isProcessing && (
                    <div
                      style={{
                        position: 'fixed',
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        cursor: 'pointer',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: '1200',
                      }}
                    >
                      <CircularProgress size={100} />
                    </div>
                  )}

                  <ResponsiveAppBar title={'Assign Checks'} pageIcon={<AttachMoney />}>
                    <Grid
                      container
                      item
                      direction="row"
                      justifyContent="flex-end"
                      spacing={2}
                    >
                      <AssignedCheckModel />
                      <Grid item>
                        <Button
                          size="small"
                          variant="contained"
                          style={{ backgroundColor: '#e0e0e0', color: '#000' }}
                          onClick={this.cancel}
                        >
                          Cancel
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          size="small"
                          color="secondary"
                          variant="contained"
                          disabled={
                            !isValid || this.state.isSaved || this.state.isProcessing
                          }
                          onClick={this.handleSave(submitForm, false)}
                        >
                          Assign Checks
                        </Button>
                      </Grid>
                    </Grid>
                  </ResponsiveAppBar>
                  <Form style={{ padding: 16 }}>
                    <Grid
                      container
                      direction="row"
                      justifyContent="space-between"
                      style={{ height: '100%' }}
                    >
                      <Grid container item xs={12} style={{ marginTop: '20px' }}>
                        <Paper>
                          <Table aria-label="simple table">
                            <TableHead>
                              <TableRow>
                                <TableCell>Location Name</TableCell>
                                <TableCell>Billing Information</TableCell>
                                <TableCell align="right">Claims Paid</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Check Number*</TableCell>
                                <TableCell>Check Date*</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {values.providers.map((provider, idx) => (
                                <TableRow key={provider.id}>
                                  <TableCell component="th" scope="row">
                                    {provider.name}
                                  </TableCell>
                                  <TableCell>
                                    {provider.billingAddress?.name != undefined &&
                                    provider.billingAddress?.name != '' ? (
                                      <>
                                        <strong>{provider.billingAddress?.name}</strong>
                                        <br></br>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                    <span>
                                      {provider.billingAddress?.addressDisplay}
                                      &nbsp;&nbsp;
                                      {provider.billingAddress?.addressDisplay != '' &&
                                      (provider.billingAddress?.line1 == undefined ||
                                        provider.billingAddress.line1 == '' ||
                                        provider.billingAddress.city == undefined ||
                                        provider.billingAddress.city == '' ||
                                        provider.billingAddress.state == undefined ||
                                        provider.billingAddress.zip == '' ||
                                        provider.billingAddress.zip == undefined) ? (
                                        <Tooltip title="Incomplete billing address">
                                          <Warning style={{ color: '#ffc107' }} />
                                        </Tooltip>
                                      ) : (
                                        <></>
                                      )}
                                    </span>
                                  </TableCell>
                                  <TableCell align="right">
                                    {provider.cmS1500s!.filter((x) => x.isChecked).length}
                                  </TableCell>
                                  <TableCell align="right">
                                    <CurrencyFormat
                                      value={this.calculateProviderTotal(provider)}
                                      thousandSeparator={true}
                                      displayType={'text'}
                                      prefix={'$'}
                                      fixedDecimalScale={true}
                                      decimalScale={2}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <FullWidthField
                                      required
                                      name={`providers[${idx}].checkNumber`}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <DatePickerField
                                      name={`providers[${idx}].datePaid`}
                                      label=""
                                      inputId={`datePaid-${idx}`}
                                      required
                                      fullWidth
                                      clearable
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow>
                                <TableCell colSpan={3} align="right">
                                  <Typography variant="body2">
                                    <strong>Total:</strong>
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2">
                                    <strong>
                                      <CurrencyFormat
                                        value={this.getTotal(values)}
                                        thousandSeparator={true}
                                        displayType={'text'}
                                        prefix={'$'}
                                        fixedDecimalScale={true}
                                        decimalScale={2}
                                      />
                                    </strong>
                                  </Typography>
                                </TableCell>
                                <TableCell colSpan={2}></TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Form>
                </>
              )
            }}
          </Formik>
        ) : (
          <></>
        )}
      </>
    )
  }
}

const InjectedWriteChecks = inject<
  IStores,
  IWriteChecksProps,
  Partial<IWriteChecksProps>,
  any
>((stores: IStores) => ({
  previewEOB: stores.checks.previewEOB,
  rerouteToPath: stores.global.rerouteToPath,
  saveCheck: stores.checks.saveCheck,
  selectedBills: stores.billsStore.selectedBills,
  openAssignedCheck: stores.billsStore.openAssignedCheckDialog,
}))(WriteChecks)

export default InjectedWriteChecks
